import type { CarrierSurchargePolicy } from '@plt/refund-quote'
import { useEffect, useRef, useState } from 'preact/hooks'
import { config } from '../config'
import {
  getCarrierSurcharges,
  setCarrierSurcharge,
  suggestedPolicy,
  type CarrierSurchargeEvidence,
  type CarrierSurchargeState,
} from './refund-surcharge-api'

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

const POLICY_LABEL: Record<CarrierSurchargePolicy, string> = {
  refunds: 'Refundable',
  withholds: 'Non-refundable',
}
const OTHER: Record<CarrierSurchargePolicy, CarrierSurchargePolicy> = { refunds: 'withholds', withholds: 'refunds' }

const fieldClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
const buttonClass =
  'px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed'
const primaryClass =
  'px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed'

/** The form's answer: a policy, or taking the carrier out of the table. */
type Answer = CarrierSurchargePolicy | 'remove'

function evidenceText(e: CarrierSurchargeEvidence | undefined): string {
  if (!e) return 'No automated quotes yet'
  const total = e.refunded + e.withheld + e.unclear
  return `Refunded ${e.refunded} · kept ${e.withheld}${e.unclear ? ` · unclear ${e.unclear}` : ''} of ${total}`
}

export function RefundSurcharges() {
  const [state, setState] = useState<CarrierSurchargeState | null>(null)
  const [loadError, setLoadError] = useState('')
  const [carrier, setCarrier] = useState('')
  const [answer, setAnswer] = useState<Answer>('refunds')
  const [reason, setReason] = useState('')
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const reasonRef = useRef<HTMLInputElement>(null)

  const load = async () => {
    try {
      setState(await getCarrierSurcharges())
      setLoadError('')
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load the YQ/YR table')
    }
  }

  useEffect(() => {
    load()
  }, [])

  const prefill = (code: string, next: Answer) => {
    setCarrier(code)
    setAnswer(next)
    setMessage(null)
    reasonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    reasonRef.current?.focus()
  }

  const save = async (e: Event) => {
    e.preventDefault()
    if (reason.trim().length < 5) {
      setMessage({ ok: false, text: 'Add a reason first. It goes in the history.' })
      return
    }
    const policy = answer === 'remove' ? null : answer
    setBusy(true)
    try {
      await setCarrierSurcharge(carrier, policy, reason.trim())
      setMessage({
        ok: true,
        text: policy
          ? `${carrier}: YQ/YR ${POLICY_LABEL[policy].toLowerCase()} from the next quote.`
          : `${carrier} removed.`,
      })
      setCarrier('')
      setReason('')
      await load()
    } catch (error) {
      setMessage({ ok: false, text: error instanceof Error ? error.message : 'Could not save the change' })
    } finally {
      setBusy(false)
    }
  }

  const evidenceFor = new Map((state?.evidence ?? []).map(e => [e.carrier, e]))
  const liveByCarrier = new Map((state?.live ?? []).map(c => [c.carrier, c]))
  const suggestions = (state?.evidence ?? []).flatMap(e => {
    const policy = suggestedPolicy(e)
    return policy && liveByCarrier.get(e.carrier)?.policy !== policy ? [{ evidence: e, policy }] : []
  })
  const carrierIsValid = /^[A-Z0-9]{2}$/.test(carrier)
  const canRemove = liveByCarrier.has(carrier)

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-8">
      <div className="flex items-center text-sm">
        <a
          href={config.routePrefix + '/refunds'}
          className="text-indigo-600 hover:text-indigo-800"
        >
          Refunds
        </a>
        <span className="mx-2 text-gray-500">/</span>
        <span className="text-gray-700">YQ/YR by carrier</span>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-gray-800">YQ/YR by carrier</h1>
        <p className="text-sm text-gray-600 mt-1">
          Whether a carrier gives back its YQ/YR surcharges when a refundable fare is refunded. For a carrier in this
          table the quote uses the answer and shows no YQ/YR range. Carriers not in it are quoted with YQ/YR kept, plus
          the &ldquo;if carrier refunds surcharges&rdquo; figure. A fare rule that says YQ or YR is non-refundable still
          wins, and non-refundable fares are not affected.
        </p>
      </div>

      {loadError && <p className="text-sm text-red-600">{loadError}</p>}
      {!state && !loadError && <p className="text-sm text-gray-500">Loading…</p>}

      {state && (
        <>
          <form
            onSubmit={save}
            className="space-y-3 p-4 bg-gray-50 border border-gray-200 rounded-md"
          >
            <h2 className="text-lg font-semibold text-gray-800">Set a carrier</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[5rem_12rem_1fr_auto] sm:items-end">
              <div>
                <label
                  htmlFor="surcharge-carrier"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Carrier
                </label>
                <input
                  id="surcharge-carrier"
                  value={carrier}
                  maxLength={2}
                  onInput={e => setCarrier((e.target as HTMLInputElement).value.toUpperCase())}
                  placeholder="QR"
                  className={fieldClass + ' font-mono uppercase'}
                />
              </div>
              <div>
                <label
                  htmlFor="surcharge-answer"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  YQ/YR
                </label>
                <select
                  id="surcharge-answer"
                  value={answer}
                  onChange={e => setAnswer((e.target as HTMLSelectElement).value as Answer)}
                  className={fieldClass}
                >
                  <option value="refunds">{POLICY_LABEL.refunds}</option>
                  <option value="withholds">{POLICY_LABEL.withholds}</option>
                  {canRemove && <option value="remove">Remove from the table</option>}
                </select>
              </div>
              <div>
                <label
                  htmlFor="surcharge-reason"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Reason
                </label>
                <input
                  id="surcharge-reason"
                  ref={reasonRef}
                  value={reason}
                  onInput={e => setReason((e.target as HTMLInputElement).value)}
                  placeholder="e.g. QR refunded YQ/YR on 6 of 8 refunds"
                  className={fieldClass}
                />
              </div>
              <button
                type="submit"
                disabled={busy || !carrierIsValid || (answer === 'remove' && !canRemove)}
                className={primaryClass}
              >
                Save
              </button>
            </div>
            {carrierIsValid && !message && (
              <p className="text-sm text-gray-500">
                {carrier}: {evidenceText(evidenceFor.get(carrier))}
              </p>
            )}
            {message && <p className={'text-sm ' + (message.ok ? 'text-green-700' : 'text-red-600')}>{message.text}</p>}
          </form>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">In the table</h2>
            {state.live.length === 0 ? (
              <p className="text-sm text-gray-500">No carriers yet. Every quote keeps YQ/YR and shows the range.</p>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="text-left px-3 py-2">Carrier</th>
                      <th className="text-left px-3 py-2">YQ/YR</th>
                      <th className="text-left px-3 py-2">Automated quotes</th>
                      <th className="text-left px-3 py-2">Last change</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {state.live.map(c => {
                      const evidence = evidenceFor.get(c.carrier)
                      const disagrees = evidence && suggestedPolicy(evidence) === OTHER[c.policy!]
                      return (
                        <tr
                          key={c.carrier}
                          className={'border-t border-gray-100 align-top' + (disagrees ? ' bg-amber-50' : '')}
                        >
                          <td className="px-3 py-2 font-mono font-medium text-gray-800">{c.carrier}</td>
                          <td className="px-3 py-2 text-gray-800 whitespace-nowrap">{POLICY_LABEL[c.policy!]}</td>
                          <td className="px-3 py-2 text-gray-600">
                            {evidenceText(evidence)}
                            {disagrees && <span className="block text-amber-700">The quotes say the opposite</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {when(c.changedAt)} by {c.changedBy}
                            <span className="block text-gray-500">{c.reason}</span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => prefill(c.carrier, OTHER[c.policy!])}
                              className={buttonClass}
                            >
                              Change
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">Suggested from automated quotes</h2>
            <p className="text-sm text-gray-600">
              {state.evidence === null
                ? 'The automated quotes could not be read just now. The table still works; reload to try again.'
                : `Carriers whose Sabre automated refund quotes in the last ${state.evidenceDays} days nearly all (90% or more, at least 5 tickets) went one way, and that aren't in the table with that answer. Carriers Sabre can't quote automatically (49005) never show here; add them above.`}
            </p>
            {state.evidence !== null && suggestions.length === 0 && (
              <p className="text-sm text-gray-500">Nothing to suggest right now.</p>
            )}
            {suggestions.length > 0 && (
              <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
                {suggestions.map(({ evidence, policy }) => (
                  <li
                    key={evidence.carrier}
                    className="flex items-center gap-3 px-3 py-2 text-sm"
                  >
                    <span className="font-mono font-medium text-gray-800 w-8">{evidence.carrier}</span>
                    <span className="text-gray-600 flex-1">{evidenceText(evidence)}</span>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => prefill(evidence.carrier, policy)}
                      className={buttonClass}
                    >
                      Use &ldquo;{POLICY_LABEL[policy]}&rdquo;
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">History</h2>
            {state.history.length === 0 ? (
              <p className="text-sm text-gray-500">No changes yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md text-sm">
                {state.history.map(h => (
                  <li
                    key={h.id}
                    className="px-3 py-2"
                  >
                    <span className="font-mono font-medium text-gray-800">{h.carrier}</span>{' '}
                    <span className="text-gray-800">{h.policy ? POLICY_LABEL[h.policy] : 'Removed'}</span>
                    <span className="text-gray-500">
                      {' '}
                      · {when(h.changedAt)} by {h.changedBy} · {h.reason}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  )
}
